import { AxiosResponse } from 'axios';
import camelCaseKeys from 'utils/camelCaseKeys';

import RestAPI from 'components/Common/restApi';
import { AnswerTypes, QuestionTypes } from 'components/CustomerIntakeSettings/requestGuide/types';

export type FormValues = {
  [name: string]: string;
};

export type HcpAssistMetadata = {
  hcpAssistOnboardingIndustryId?: string;
  hcpAssistOnboardingCustomerTypeId?: string;
  hcpAssistOnboardingCategoryId?: string;
  hcpAssistOnboardingServiceId?: string;
  hcpAssistOnboardingHoursType?: string;
  hcpAssistOnboardingScheduleType?: string;
};

export type RequestGuideAnswer = {
  uid: string;
  value: string;
  hcpAssistMetadata?: HcpAssistMetadata;
};

type ChildNodesResponse = {
  [id: string]: RequestGuideNodeResponse[];
};

export type RequestGuideNodeQuestionResponse = {
  uid: string;
  parent_id?: string | undefined;
  parent_answer_id?: string | undefined;
  type: QuestionType;
  question_type: QuestionTypes;
  answer_type: AnswerTypes;
  reminder?: string | undefined;
  question: string;
  answers?: RequestGuideAnswer[];
  child_nodes?: ChildNodesResponse;
};

export type RequestGuideNodeReminderResponse = {
  uid: string;
  parent_id?: string | undefined;
  parent_answer_id?: string | undefined;
  type: ReminderType;
  title: string;
  note: string;
};

export type ChildNodes = {
  [id: string]: RequestGuideNode[];
};

export const QUESTION_TYPE = 'question' as const;
export const REMINDER_TYPE = 'reminder' as const;
export type QuestionType = typeof QUESTION_TYPE;
export type ReminderType = typeof REMINDER_TYPE;
export type MoveDirection = 'up' | 'down';

export type RequestGuideNodeQuestion = {
  uid: string;
  parentId?: string | undefined;
  parentAnswerId?: string | undefined;
  type: QuestionType;
  questionType: QuestionTypes;
  answerType: AnswerTypes;
  reminder?: string | undefined;
  question: string;
  answers?: RequestGuideAnswer[];
  childNodes?: ChildNodes;
};

export type RequestGuideNodeReminder = {
  uid: string;
  parentId?: string | undefined;
  parentAnswerId?: string | undefined;
  type: ReminderType;
  title: string;
  note: string;
};

export type RequestGuideNodeResponse = RequestGuideNodeQuestionResponse | RequestGuideNodeReminderResponse;
export type RequestGuideNode = RequestGuideNodeQuestion | RequestGuideNodeReminder;
export type RequestGuideNodeAnsweredResponse = RequestGuideNodeQuestionResponse & {
  selected_answer?: string;
};

export interface GetRequestGuideData {
  requestGuideNodes: Array<RequestGuideNode>;
}

type SaveRequestGuideAnswersData = {
  request_id: string;
  request_type: 'Job' | 'Estimate' | 'Lead';
  answers_content: RequestGuideNodeAnsweredResponse[];
};

export const uncamelizeNodes = (data: RequestGuideNode): RequestGuideNodeResponse => {
  if (data.type === REMINDER_TYPE) {
    return {
      parent_answer_id: data.parentAnswerId,
      parent_id: data.parentId,
      ...data,
    };
  }

  const { childNodes, answerType, questionType, parentAnswerId, parentId, ...rest } = data;

  return {
    parent_answer_id: parentAnswerId,
    parent_id: parentId,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    child_nodes: getChildNodes(childNodes),
    answer_type: answerType,
    question_type: questionType,
    ...rest,
  };
};

function getChildNodes(childNodes: ChildNodes = {}): ChildNodesResponse {
  return Object.entries(childNodes).reduce(
    (current, [key, nodes]) => ({
      ...current,
      [key]: nodes.map(uncamelizeNodes),
    }),
    {} as ChildNodesResponse
  );
}

export const getRequestGuide = async (): Promise<RequestGuideNode[]> =>
  camelCaseKeys(
    (
      await RestAPI.get<{ request_guide_nodes: RequestGuideNode[] }>(
        '/alpha/customer_intake_flow/settings/request_guide'
      )
    ).data.request_guide_nodes
  );

export const saveRequestGuideNode = (data: RequestGuideNode): Promise<AxiosResponse<void>> => {
  const node: RequestGuideNodeResponse = uncamelizeNodes(data);

  return RestAPI.postWithFormData('/alpha/customer_intake_flow/settings/request_guides/nodes', { node });
};

export const editRequestGuideNode = (data: RequestGuideNode): Promise<AxiosResponse<void>> => {
  const node: RequestGuideNodeResponse = uncamelizeNodes(data);

  return RestAPI.updateWithFormData('/alpha/customer_intake_flow/settings/request_guides/nodes', { node });
};

export const moveNode = async (uid: string, direction: MoveDirection) =>
  RestAPI.postWithFormData('/alpha/customer_intake_flow/settings/request_guides/nodes/move', {
    uid,
    direction,
  });

export const deleteNode = async (uid: string) =>
  RestAPI.deleteWithFormData('/alpha/customer_intake_flow/settings/request_guides/nodes', {
    uid,
  });

export const saveRequestGuideAnswers = (data: SaveRequestGuideAnswersData): Promise<AxiosResponse<void>> =>
  RestAPI.postWithFormData('/alpha/customer_intake_flow/request_guide_answers', data);
